import React, { useState } from 'react'
import classNames from 'classnames';

import Form from '../forms/Form';
import TextField from '../forms/TextField';
import Checkbox from '../forms/Checkbox';
import { minLength, validEmail, required } from '../forms/validations';
import PlanSalesSummary from './PlanSalesSummary';
import SignupFormProgressIndicator from './SignupFormProgressIndicator';
import StripeCardField from './StripeCardField';
import { getSalesText } from '../../shared/plans';
import poweredByStripe from '../../images/stripe-mark-blurple.svg';
import posthogClient from '../../shared/posthogClient';
import visa from '../../images/visa.svg';
import amex from '../../images/amex.svg';
import mastercard from '../../images/mastercard.svg';
import discover from '../../images/discover.svg';
import { enroll, createUserAndSubscription, completeRegistration } from './backend';
import useReCAPTCHA from "./useReCAPTCHA";
import SignUpForm from './SignUpForm';
import { getStripe, buildStripeError } from '../stripe';

const stageNames = ["details", "billing", "done"];

export default function CreateUserAndSubscribe({user, priceId}) {

  const initialFormValues = {
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    newsletter: false,
  }

  const salesText = getSalesText(priceId, 'fresh');

  const [ CardElement, SetCardElement ] = useState(null);
  const [ Progress, SetProgress ] = useState(1);
  const [ ActiveStage, SetActiveStage ] = useState(1);
  const [ Processing, SetProcessing ] = useState(false);
  const [ Success, SetSuccess ] = useState(false);
  const [ FormData, SetFormData ] = useState(initialFormValues);
  const [ Errors, SetErrors ] = useState({});
  const [ CachedClientSecret, SetCachedClientSecret ] = useState(null);
  const [ CachedRegistrationToken, SetCachedRegistrationToken ] = useState(null);

  const {
    ReCAPTCHAComponent,
    getReCAPTCHAToken,
  } = useReCAPTCHA(__WORDTRACKER_INVISIBLE_RECAPTCHA_SITE_KEY)

  const goToStage = (stage, advanceProgress = false) => {

    SetProcessing(false);

    const lastStageNum = stageNames.length;

    if (stage === ActiveStage) return false;

    if (stage > lastStageNum || stage < 1) return false;

    if (ActiveStage === lastStageNum) return false;

    if (!advanceProgress && stage > Progress) return false;

    if (stage > Progress) SetProgress(stage);

    SetActiveStage(stage);

    // SetStageName(stageNames[stage - 1]);

  };

  const handleChangeField = e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    SetFormData({
      ...FormData,
      [e.target.name]: value
    });
  }

  const handleBackendError = errors => {
    SetErrors(errors);
    SetProcessing(false);
    if ((errors.email || errors.password) && ActiveStage != 1) {
      goToStage(1);
    }
  }

  const handleErrorStateChange = (e, error) => {
    if (error) console.error(error);
    const fieldName = [e.target.name];
    let _errors = { ...Errors, [fieldName]: error };
    _errors = Object.fromEntries(Object.entries(_errors).filter(([key, value]) => value !== null));
    SetErrors(_errors);
  }


  const detailsComplete = () => {
    posthogClient.capture('signup_modal_stage_two');
    SetProcessing(false);
    return goToStage(2, true);
  }

  const subscribeComplete = (createdUser) => {
    SetProcessing(false);
    SetSuccess(true);
    goToStage(3, true);
    posthogClient.capture('signup_modal_complete');
    const loginUrl = `${__ZUUL_URL}/consumer_search?user_email=${encodeURIComponent(createdUser.email)}&user_token=${createdUser.token}`;
    setTimeout(() => window.location.href = loginUrl, 1200);
  }

  const handleSubmitDetails = async () => {
    SetErrors({});
    SetProcessing(true);
    const token = await getReCAPTCHAToken();
    try {
      await enroll(token, {email: FormData.email, newsletter: FormData.newsletter});
      return detailsComplete();
    } catch(error) {
      return handleBackendError(error);
    }
  }

  const confirmCardPayment = async (client_secret) => {
    const stripe = await getStripe();
    const cardDetails = { payment_method: { card: CardElement, billing_details: { name: `${FormData.first_name} ${FormData.last_name}` } } }
    return await stripe.confirmCardPayment(client_secret, cardDetails);
  }

  const handleSubmitCard = async () => {
    SetErrors({});
    SetProcessing(true);
    
    try {
      let client_secret = CachedClientSecret;
      let registration_token = CachedRegistrationToken;

      if (!client_secret || !registration_token) {
        const token = await getReCAPTCHAToken();
        const {user: _user, client_secret: _client_secret} = await createUserAndSubscription(token, { ...FormData, plan_code: priceId });
        client_secret = _client_secret;
        registration_token = _user.registration_token;
        
        // Cache the values
        SetCachedClientSecret(client_secret);
        SetCachedRegistrationToken(registration_token);
      }
      
      const stripeResponse = await confirmCardPayment(client_secret);
      const stripeError = buildStripeError(stripeResponse);
      if (stripeError) return handleBackendError({card_number: stripeError});
      
      const { user: createdUser } = await completeRegistration({ ...FormData, registration_token, plan_code: priceId, payment_method_id: stripeResponse.paymentIntent.payment_method });
      return subscribeComplete(createdUser);
    } catch(error) {
      return handleBackendError(error);
    }
  }

  const filledOut = (FormData.email.length > 0 && FormData.password.length > 0);
  const isSubmitDisabled = Errors.email || Errors.password || !filledOut || Processing;

  const renderDetailsForm = () => {

    posthogClient.capture('signup_modal_stage_one');

    return (

      <div className='signup-form-area details fade-in' style={{display: stageNames[ActiveStage - 1] === 'details' ? 'block' : 'none'}}>

        {salesText.subtitle && <p className='subtitle'>{salesText.subtitle}</p>}

        <div className='two-blocks'>

          <div className='signup-block stripe-form-area'>

            {salesText.titleDetails && <p className='sales-text'>{salesText.titleDetails}</p> }

            <div className="form-row">
              <TextField
                name='email'
                type='text'
                label='Email address'
                klassname='email'
                initialValue={FormData.email}
                onErrorStateChange={handleErrorStateChange}
                onChange={handleChangeField}
                error={Errors.email === 'already taken' ? <>already taken. <a href={`${__ZUUL_URL}/login`}>Log in here.</a></> : Errors.email}
                validations={[ required(), validEmail() ]}
                placeholder='name@example.com'
              />
            </div>

            <div className="form-row">
              <TextField
                name='password'
                type='password'
                label='Password'
                klassname='password'
                initialValue={FormData.password}
                onErrorStateChange={handleErrorStateChange}
                onChange={handleChangeField}
                validations={[ minLength(6) ]}
                error={Errors.password}
              />
            </div>

            <div className="form-row checkbox-row">
              <Checkbox
                name='newsletter'
                label={<span>Please keep in touch with the latest SEO updates</span>}
                klassname='newsletter'
                initialValue={FormData.newsletter}
                onErrorStateChange={handleErrorStateChange}
                onChange={handleChangeField}
                error={Errors.newsletter}
              />
            </div>

            <button
              className='subscribe-standard standard'
              type='button'
              disabled={isSubmitDisabled}
              onClick={handleSubmitDetails}
            >{Processing ? 'Processing...' : salesText.buttonDetails}</button>

            {salesText.buttonDetailsHint && <span className='button-hint'>{salesText.buttonDetailsHint}</span>}

          </div>

          <PlanSalesSummary priceId={priceId} user={user} country={FormData.country} />

        </div>

        <span className='continue-button-note switch-to-monthly nolink'>Need more results or territories? <a href={`${__ZUUL_URL}/sign-up`} target='_self'>Check out all our plans here</a>.</span>

      </div>
    )
  }

  const renderBillingForm = () => {

    const isBillingDisabled = !FormData.first_name || !FormData.last_name;

    return (<div className='signup-form-area billing fade-in' style={{display: stageNames[ActiveStage - 1] === 'billing' ? 'block' : 'none'}}>

      {salesText.subtitle && <p className='subtitle'>{salesText.subtitle}</p>}

      <div className='two-blocks'>

        <div className='signup-block stripe-form-area'>

          {salesText.titleBilling && <p className='sales-text'>{salesText.titleBilling}</p> }

          <div className='form-row'>
            <label className={`number ${classNames({"has_error": Errors.card_number})}`}>
              Card details&nbsp;
              { Errors.card_number && <span className='error'>{Errors.card_number}</span> }
              <StripeCardField
                cardElement={CardElement}
                onCardElement={SetCardElement}/>
            </label>
          </div>

          <div className='form-row dual-row'>
            
            <TextField
              name='first_name'
              type='text'
              label='First name'
              klassname='first-name'
              initialValue={FormData.first_name}
              onErrorStateChange={handleErrorStateChange}
              onChange={handleChangeField}
              error={Errors.first_name}
              validations={[ required() ]}
            />

            <TextField
              name='last_name'
              type='text'
              label='Last name'
              klassname='last-name'
              initialValue={FormData.last_name}
              onErrorStateChange={handleErrorStateChange}
              onChange={handleChangeField}
              error={Errors.last_name}
              validations={[ required() ]}
            />

          </div>

          <div className='stripe-trust'>
            <h5>
              <i className="fa-solid fa-lock"></i>
              Guaranteed <strong>safe and secure</strong> checkout
            </h5>
            <div className="trust-icons">
              <img className='powered-by-stripe' src={poweredByStripe} alt='Powered By Stripe' />
              <img className='card-icon' src={visa} alt='Visa card icon' />
              <img className='card-icon' src={mastercard} alt='Mastercard card icon' />
              <img className='card-icon' src={amex} alt='Amex card icon' />
              <img className='card-icon' src={discover} alt='Discover card icon' />
            </div>
          </div>

          <div className='form-row'>
            <button
              onClick={handleSubmitCard}
              className={`subscribe-standard standard ${classNames({success: Success})}`}
              disabled={Processing || Success || isBillingDisabled }
            >{ Success ? "Success!" : Processing ? "Processing..." : salesText.buttonBilling }</button>
            {salesText.buttonHint && <span className='button-hint'>{salesText.buttonHint}</span>}
          
          </div>

        </div>

        <PlanSalesSummary priceId={priceId} user={user} country={FormData.country} />

      </div>

      <span className='continue-button-note switch-to-monthly nolink'>Need more results or territories? <a href={`${__ZUUL_URL}/sign-up`} target='_self'>Check out all our plans here</a>.</span>

    </div>
  )}

  const renderDone = () => (
    <div className='signup-form-area done fade-in' style={{display: stageNames[ActiveStage - 1] === 'done' ? 'block' : 'none'}}>
      <div className='big-tick'></div>
      <h2>Sign-up complete.</h2>
      {salesText.doneHtml}
    </div>
  )

  return (

    <SignUpForm title={salesText.title} showTitle={stageNames[ActiveStage - 1] != 'done'}>

      {stageNames[ActiveStage - 1] != 'done' && salesText.priceMark}

      <Form name='stripeForm' id='stripe-form-signup'>

        {ReCAPTCHAComponent}

        <div className='signup-form-stage-holder'>

          { renderDetailsForm() }

          { renderBillingForm() }

          { renderDone() }

          <input type="hidden" name="stripe-token" />

        </div>

      </Form>

      <SignupFormProgressIndicator
        onClickNode={goToStage}
        progress={Progress}
        activeStage={ActiveStage}
      />

    </SignUpForm>

  )
}